import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function FAQManager({ authToken }) {
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ question: "", answer: "" });

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${authToken}` }
  }), [authToken]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/faq/categories`, authHeaders());
      setCategories(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [authHeaders]);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/faq/entries`, authHeaders());
      setEntries(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [authHeaders]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchEntries()]);
      setLoading(false);
    };
    load();
  }, [fetchCategories, fetchEntries]);

  // Kategorie erstellen
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API_BASE}/api/faq/categories`, { name: newCategoryName }, authHeaders());
      toast({ title: "Kategorie erstellt" });
      setNewCategoryName("");
      fetchCategories();
    } catch (e) {
      toast({ title: "Fehler beim Erstellen", variant: "destructive" });
    }
  };

  // Kategorie aktualisieren
  const handleUpdateCategory = async (id, name) => {
    try {
      await axios.put(`${API_BASE}/api/faq/categories/${id}`, { name }, authHeaders());
      toast({ title: "Kategorie aktualisiert" });
      setEditingCategory(null);
      fetchCategories();
    } catch (e) {
      toast({ title: "Fehler beim Aktualisieren", variant: "destructive" });
    }
  };

  // Kategorie löschen
  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Kategorie "${name}" wirklich löschen?\n\nAlle zugehörigen FAQ-Einträge werden ebenfalls gelöscht!`)) return;
    try {
      await axios.delete(`${API_BASE}/api/faq/categories/${id}`, authHeaders());
      toast({ title: "Kategorie gelöscht" });
      if (selectedCategory === id) setSelectedCategory(null);
      fetchCategories();
      fetchEntries();
    } catch (e) {
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  // Eintrag erstellen
  const handleCreateEntry = async () => {
    if (!newEntry.question.trim() || !selectedCategory) return;
    try {
      await axios.post(`${API_BASE}/api/faq/entries`, {
        category_id: selectedCategory,
        question: newEntry.question,
        answer: newEntry.answer
      }, authHeaders());
      toast({ title: "FAQ-Eintrag erstellt" });
      setNewEntry({ question: "", answer: "" });
      setShowNewEntry(false);
      fetchEntries();
    } catch (e) {
      toast({ title: "Fehler beim Erstellen", variant: "destructive" });
    }
  };

  // Eintrag aktualisieren
  const handleUpdateEntry = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/api/faq/entries/${id}`, data, authHeaders());
      toast({ title: "FAQ-Eintrag aktualisiert" });
      setEditingEntry(null);
      fetchEntries();
    } catch (e) {
      toast({ title: "Fehler beim Aktualisieren", variant: "destructive" });
    }
  };

  // Eintrag löschen
  const handleDeleteEntry = async (id, question) => {
    if (!window.confirm(`FAQ-Eintrag "${question.substring(0, 50)}..." wirklich löschen?`)) return;
    try {
      await axios.delete(`${API_BASE}/api/faq/entries/${id}`, authHeaders());
      toast({ title: "FAQ-Eintrag gelöscht" });
      fetchEntries();
    } catch (e) {
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  const categoryEntries = entries.filter(e => e.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-violet-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="faq-manager">
      {/* Kategorien */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          FAQ-Kategorien
        </h3>

        {/* Neue Kategorie */}
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Neue Kategorie..."
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            onKeyDown={(e) => e.key === "Enter" && handleCreateCategory()}
          />
          <button
            onClick={handleCreateCategory}
            disabled={!newCategoryName.trim()}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50"
          >
            Hinzufügen
          </button>
        </div>

        {/* Kategorie-Liste */}
        {categories.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Noch keine Kategorien vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className={`flex items-center gap-2 rounded-lg border p-3 transition-colors cursor-pointer ${
                  selectedCategory === cat.id
                    ? "border-violet-500 bg-violet-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {editingCategory === cat.id ? (
                  <input
                    type="text"
                    defaultValue={cat.name}
                    className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => handleUpdateCategory(cat.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdateCategory(cat.id, e.target.value);
                      if (e.key === "Escape") setEditingCategory(null);
                    }}
                  />
                ) : (
                  <span className="flex-1 text-sm font-medium text-slate-700">{cat.name}</span>
                )}
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  {entries.filter(e => e.category_id === cat.id).length}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingCategory(cat.id); }}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title="Bearbeiten"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id, cat.name); }}
                  className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Löschen"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ-Einträge für ausgewählte Kategorie */}
      {selectedCategory && (
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              FAQ-Einträge: {categories.find(c => c.id === selectedCategory)?.name}
            </h3>
            <button
              onClick={() => setShowNewEntry(true)}
              className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Neuer Eintrag
            </button>
          </div>

          {/* Neuer Eintrag Formular */}
          {showNewEntry && (
            <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <h4 className="mb-3 text-base font-semibold text-emerald-800">Neuer FAQ-Eintrag</h4>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Frage</label>
                  <input
                    type="text"
                    value={newEntry.question}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, question: e.target.value }))}
                    placeholder="Wie kann ich...?"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Antwort</label>
                  <ModernRichTextEditor
                    value={newEntry.answer}
                    onChange={(val) => setNewEntry(prev => ({ ...prev, answer: val }))}
                    placeholder="Antwort eingeben..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setShowNewEntry(false); setNewEntry({ question: "", answer: "" }); }}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleCreateEntry}
                    disabled={!newEntry.question.trim()}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    Erstellen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Einträge-Liste */}
          {categoryEntries.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Noch keine Einträge in dieser Kategorie.</p>
          ) : (
            <div className="space-y-3">
              {categoryEntries.map((entry) => (
                <FAQEntryCard
                  key={entry.id}
                  entry={entry}
                  isEditing={editingEntry === entry.id}
                  onEdit={() => setEditingEntry(entry.id)}
                  onSave={(data) => handleUpdateEntry(entry.id, data)}
                  onCancel={() => setEditingEntry(null)}
                  onDelete={() => handleDeleteEntry(entry.id, entry.question)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Einzelner FAQ-Eintrag
function FAQEntryCard({ entry, isEditing, onEdit, onSave, onCancel, onDelete }) {
  const [editData, setEditData] = useState({ question: entry.question, answer: entry.answer });

  useEffect(() => {
    setEditData({ question: entry.question, answer: entry.answer });
  }, [entry]);

  if (isEditing) {
    return (
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Frage</label>
            <input
              type="text"
              value={editData.question}
              onChange={(e) => setEditData(prev => ({ ...prev, question: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600">Antwort</label>
            <ModernRichTextEditor
              value={editData.answer}
              onChange={(val) => setEditData(prev => ({ ...prev, answer: val }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={onCancel}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Abbrechen
            </button>
            <button
              onClick={() => onSave(editData)}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500"
            >
              Speichern
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h4 className="text-base font-semibold text-slate-800">{entry.question}</h4>
          <div 
            className="mt-2 text-sm text-slate-600 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: entry.answer }}
          />
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="rounded p-1.5 text-slate-400 hover:bg-white hover:text-slate-600"
            title="Bearbeiten"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Löschen"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
